import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function formatJoinDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Profile() {
  const { user, logout, updateDisplayName } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const displayName = user?.name || 'Survivor';
  const initial = (user?.name || user?.email || '?')[0].toUpperCase();
  const joined = formatJoinDate(user?.joinedAt);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const result = await updateDisplayName(name);
    setSaving(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setIsEditing(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--brand-bg)' }}>
      <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
        <span className="pill">Your Account</span>
        <h1 className="page-title section-gap">Profile</h1>
        <p className="page-subtitle">Manage how you appear across HiveFall.</p>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
            <span
              className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)] text-3xl font-black text-white"
            >
              {initial}
            </span>
            <div>
              <p className="text-xl font-black text-slate-900">{displayName}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
              {joined && <p className="mt-1 text-xs font-semibold text-slate-400">Commander since {joined}</p>}
            </div>
          </div>

          <div className="mt-8 border-t border-slate-100 pt-6">
            {isEditing ? (
              <form onSubmit={handleSave} className="max-w-sm">
                <div className="form-row">
                  <label htmlFor="displayName">Display name</label>
                  <input
                    id="displayName"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input"
                    placeholder="Your name"
                    required
                  />
                </div>

                {error && (
                  <div className="error-box">
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {error}
                  </div>
                )}

                <div className="flex gap-2">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ background: '#f1f5f9', color: '#334155' }}
                    onClick={() => {
                      setIsEditing(false);
                      setName(user?.name || '');
                      setError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                type="button"
                className="btn"
                style={{ background: '#f1f5f9', color: '#334155' }}
                onClick={() => setIsEditing(true)}
              >
                Edit display name
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
          >
            Log out
          </button>
          <Link to="/" className="text-sm font-bold text-[var(--brand-primary)] hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
